import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useTranslation } from 'react-i18next';
import { Phone, Copy, Key, CheckCircle, AlertCircle, QrCode, X, Power, Calendar } from 'lucide-react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { baseURL } from '../utils/api';
import '../styles/dashboard.css';

const API_URL = baseURL;

export default function LinkPhoneNumbers() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const navigate = require('react-router-dom').useNavigate();

    const [phones, setPhones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userPlan, setUserPlan] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [sessionId, setSessionId] = useState('');
    const [qrCode, setQrCode] = useState(null);
    const [qrLoading, setQrLoading] = useState(false);
    const [qrSessionId, setQrSessionId] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [error, setError] = useState(null);
    const [generatedApiKey, setGeneratedApiKey] = useState(null);
    const [generatedCurlExample, setGeneratedCurlExample] = useState(null);
    const [currentPhoneId, setCurrentPhoneId] = useState(null);
    const [reconnectingPhoneId, setReconnectingPhoneId] = useState(null);
    const [registrationStep, setRegistrationStep] = useState(0); // 0: idle, 1: registering, 2: generating qr, 3: loading qr

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchPhones();
        fetchUserPlan();

        // Poll for status updates every 5 seconds to catch real-time changes
        const statusInterval = setInterval(() => {
            fetchPhones();
        }, 5000);

        return () => clearInterval(statusInterval);
    }, [isAuthenticated, navigate]);

    const fetchPhones = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/phone-numbers/list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const phonesData = res.data.data || [];
            
            // Fetch all API keys once
            const keyRes = await axios.get(`${API_URL}/api-keys/list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const allKeys = keyRes.data.data || [];
            
            // Attach ALL API keys to each phone (not specific matching)
            const phonesWithKeys = phonesData.map((phone) => {
                // Get the first API key (user's main API key)
                const apiKey = allKeys[0];
                
                if (apiKey) {
                    // Private keys use v1/private/send-otp, public keys use v2/send-otp
                    const isPrivate = apiKey.is_private === true;
                    const endpoint = isPrivate ? 'https://safrjal.com/api/v1/private/send-otp' : 'https://safrjal.com/api/v2/send-otp';
                    
                    // Format curl with proper multiline format
                    const curlExample = `curl --location '${endpoint}' \\
--header 'Content-Type: application/json' \\
--header 'X-API-Key: ${apiKey.key_value}' \\
--data '{
    "phone": "${phone.phone || '962795021746'}",
    "otp": "4850",
    "title": "تطبيق فيسبوك"
}'`;
                    
                    return {
                        ...phone,
                        api_key: apiKey.key_value,
                        is_private: isPrivate,
                        curl_example: curlExample
                    };
                }
                
                return phone;
            });
            
            setPhones(phonesWithKeys);
        } catch (error) {
            console.error('Error fetching phones:', error);
            setPhones([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserPlan = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/plans/current`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setUserPlan(res.data.data);
        } catch (error) {
            console.error('Error fetching plan:', error);
            // Set default plan limit
            setUserPlan({ max_phone_numbers: 1 });
        }
    };

    const generateUniqueSessionId = () => {
        return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

    const handleAddPhone = (e) => {
        e.preventDefault();
        const uniqueSessionId = generateUniqueSessionId();
        setSessionId(uniqueSessionId);
        setShowAddModal(true);
        setError(null);
    };

    // Fetch QR code for a session
    const fetchQRCode = async (sessionId) => {
        try {
            const response = await axios.get(`${API_URL}/api/qr/${sessionId}`);
            console.log('📥 QR Response:', response.data);
            
            if (response.data.qr) {
                console.log('✅ QR code received');
                setQrCode(response.data.qr);
                setQrLoading(false);
                return true;
            } else if (response.data.status === 'ready' || response.data.is_ready === true) {
                console.log('✅ Account is ready - status:', response.data.status, 'phone:', response.data.phone);
                // Account is ready - confirm phone in backend and close QR modal
                
                if (response.data.phone) {
                    // Confirm the phone with backend
                    try {
                        console.log(`📤 Confirming phone: ${response.data.phone}`);
                        const confirmResponse = await axios.post(`${API_URL}/phone-numbers/confirm-phone`, {
                            sessionId,
                            phone: response.data.phone
                        }, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                        });
                        
                        console.log(`✅ Phone confirmation response:`, confirmResponse.data);
                    } catch (err) {
                        console.error(`❌ Error confirming phone:`, err);
                        // Don't fail - continue anyway
                    }
                }
                
                setShowQRModal(false);
                setQrCode(null);
                
                // Wait a moment for database to commit
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Fetch phones to get the newly created phone ID
                const token = localStorage.getItem('token');
                const phonesRes = await axios.get(`${API_URL}/phone-numbers/list`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const newPhones = phonesRes.data.data || [];
                console.log('📱 Fetched phones after auth:', newPhones);
                setPhones(newPhones);
                
                // Find the phone with matching sessionId
                const newPhone = newPhones.find(p => p.sessionId === sessionId);
                console.log(`🔍 Looking for phone with sessionId ${sessionId}, found:`, newPhone);
                
                if (newPhone) {
                    console.log(`✅ Found phone ${newPhone.id}, generating API key...`);
                    await generateApiKeyForPhone(newPhone.id);
                } else {
                    console.warn(`⚠️ Phone with sessionId ${sessionId} not found in list. Reconnecting:`, reconnectingPhoneId);
                    // For reconnection, use the reconnectingPhoneId directly if phone not found
                    if (reconnectingPhoneId) {
                        console.log(`✅ Using reconnectingPhoneId ${reconnectingPhoneId}, generating API key...`);
                        await generateApiKeyForPhone(reconnectingPhoneId);
                    }
                }
                return false;
            }
            return true; // Continue polling
        } catch (error) {
            console.warn('QR code not ready yet, will retry...', error.message);
            return true; // Continue polling
        }
    };

    // Generate API key for the newly linked phone
    const generateApiKeyForPhone = async (phoneId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/phone-numbers/generate-key`, 
                { phoneNumberId: phoneId },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (response.data.success) {
                console.log('✅ API key generated successfully');
                
                // Refresh the phones list to get the phone details
                await fetchPhones();
                
                // Find the newly created phone to get its details
                const token2 = localStorage.getItem('token');
                const phonesRes = await axios.get(`${API_URL}/phone-numbers/list`, {
                    headers: { 'Authorization': `Bearer ${token2}` }
                });
                const phones = phonesRes.data.data || [];
                const newPhone = phones.find(p => p.id === phoneId);
                
                if (newPhone && response.data.apiKey) {
                    // Generate CURL example
                    const endpoint = 'https://safrjal.com/api/v1/private/send-otp';
                    const curlExample = `curl --location '${endpoint}' \\
--header 'Content-Type: application/json' \\
--header 'X-API-Key: ${response.data.apiKey}' \\
--data '{
    "phone": "${newPhone.phone_number || '962795021746'}",
    "otp": "4850",
    "title": "تطبيق فيسبوك"
}'`;
                    
                    // Set the generated API key and show success modal
                    setGeneratedApiKey(response.data.apiKey);
                    setGeneratedCurlExample(curlExample);
                    setCurrentPhoneId(phoneId);
                    setShowSuccessModal(true);
                }
            }
        } catch (error) {
            console.error('Error generating API key:', error);
            // Refresh anyway to show updated state
            await fetchPhones();
        }
    };

    // Poll for QR code after registration
    useEffect(() => {
        if (!qrSessionId || !showQRModal) return;

        let isMounted = true;
        let pollCount = 0;
        const maxPolls = 240; // Max 240 seconds of polling (4 minutes)

        const pollQR = async () => {
            pollCount++;
            if (pollCount > maxPolls) {
                console.error('QR code polling timeout');
                isMounted && setQrLoading(false);
                return;
            }

            const shouldContinue = await fetchQRCode(qrSessionId);
            if (isMounted && shouldContinue) {
                // Aggressive polling: 500ms for first 20 polls, then 1s for next 40, then 2s
                let nextDelay = 500;
                if (pollCount > 20) nextDelay = 1000;
                if (pollCount > 60) nextDelay = 2000;
                
                setTimeout(() => pollQR(), nextDelay);
            }
        };

        // Start polling immediately
        setQrLoading(true);
        console.log('🔄 Starting QR polling for sessionId:', qrSessionId);
        pollQR();

        return () => {
            isMounted = false;
        };
    }, [qrSessionId, showQRModal, fetchQRCode]);

    // Handle form submission for adding phone
    const handleSubmitAddPhone = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        setRegistrationStep(1); // Step 1: Registering

        try {
            const token = localStorage.getItem('token');
            setRegistrationStep(1); // Registering phase
            const response = await axios.post(`${API_URL}/phone-numbers/create`, {
                sessionId
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                setRegistrationStep(2); // Step 2: Generating QR
                // Show QR modal and start fetching QR code
                setQrSessionId(sessionId);
                setQrCode(null);
                setShowQRModal(true);
                setShowAddModal(false);
                setRegistrationStep(3); // Step 3: Loading QR (in modal now)
                
                // Start fetching QR code immediately
                await fetchQRCode(sessionId);
                
                setSessionId('');
                fetchPhones();
            } else {
                setError(response.data.error || 'Failed to create phone entry');
                setRegistrationStep(0);
            }
        } catch (error) {
            console.error('Error adding phone:', error);
            setError(error.response?.data?.error || 'Error adding phone');
            setRegistrationStep(0);
        } finally {
            setFormLoading(false);
        }
    };

    const handleCopyKey = (key) => {
        navigator.clipboard.writeText(key);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const handleReconnectPhone = async (phoneId) => {
        try {
            const token = localStorage.getItem('token');
            setReconnectingPhoneId(phoneId);
            
            // Clear old QR data immediately
            setQrCode(null);
            setQrLoading(true);

            // Request new QR code for reconnection
            const response = await axios.post(`${API_URL}/phone-numbers/reconnect`, 
                { phoneNumberId: phoneId },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (response.data.success) {
                setQrSessionId(response.data.sessionId);
                setShowQRModal(true);
                console.log('✅ Reconnection initiated, session:', response.data.sessionId);
                // QR loading will continue via the polling effect
            }
        } catch (error) {
            console.error('Error reconnecting phone:', error);
            setQrLoading(false);
            alert(t('Failed to reconnect phone. Please try again.'));
        }
    };

    const handleCopyCurl = (curl) => {
        navigator.clipboard.writeText(curl);
        alert(t('CURL command copied to clipboard'));
    };

    // Pause phone account
    const handlePausePhone = async (phoneId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API_URL}/phone-numbers/${phoneId}/pause`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                setPhones(phones.map(p => p.id === phoneId ? { ...p, status: 'paused' } : p));
                console.log('✅ Phone paused successfully');
            } else {
                alert(response.data.error || 'Failed to pause phone');
            }
        } catch (error) {
            console.error('Pause error:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Error pausing phone';
            alert(errorMsg);
        }
    };

    // Resume phone account (no QR needed)
    const handleResumePhone = async (phoneId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API_URL}/phone-numbers/${phoneId}/resume`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                setPhones(phones.map(p => p.id === phoneId ? { ...p, status: response.data.phone.status || 'verified' } : p));
                console.log('✅ Phone resumed successfully');
            } else {
                alert(response.data.error || 'Failed to resume phone');
            }
        } catch (error) {
            console.error('Resume error:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Error resuming phone';
            alert(errorMsg);
        }
    };

    const closeQRModal = () => {
        setShowQRModal(false);
        setQrCode(null);
        setQrSessionId(null);
        setQrLoading(false);
        setReconnectingPhoneId(null);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setSessionId('');
        setError(null);
        setRegistrationStep(0);
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);
        setGeneratedApiKey(null);
        setGeneratedCurlExample(null);
        setCurrentPhoneId(null);
    };

    const handleCopyGeneratedKey = () => {
        navigator.clipboard.writeText(generatedApiKey);
        setCopiedKey(generatedApiKey);
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const handleCopyGeneratedCurl = () => {
        navigator.clipboard.writeText(generatedCurlExample);
        alert(t('CURL command copied to clipboard'));
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-[#2D3748] border-t-[#128C7E] rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-[#A0AEC0]">{t('Loading...')}</p>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    const canAddMore = userPlan && phones.filter(p => p.is_active).length < userPlan.max_phone_numbers;

    return (
        <DashboardLayout>
            <div className="bg-[#0B0E14] min-h-screen py-6 sm:py-12 px-4 sm:px-6 md:px-12">
                {/* Gradient accent overlay */}
                <div className="fixed top-0 left-0 w-full h-96 bg-gradient-to-br from-[#128C7E]/5 via-transparent to-transparent pointer-events-none"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Header */}
                    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 sm:mb-12 gap-4 sm:gap-6 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                        <div className={`flex items-start gap-3 sm:gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#128C7E]/20 rounded-lg sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                                <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-[#128C7E]" />
                            </div>
                            <div style={{direction: isRTL ? 'rtl' : 'ltr'}}>
                                <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white">{t('Link Phone Numbers')}</h1>
                                <p className="text-xs sm:text-lg text-[#A0AEC0] mt-1 sm:mt-2">{t('Connect your WhatsApp phone numbers to send OTP messages')}</p>
                            </div>
                        </div>
                        {canAddMore && (
                            <button 
                                className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-[#128C7E] text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20 flex-shrink-0 whitespace-nowrap ${isRTL ? 'flex-row-reverse' : ''}`}
                                onClick={handleAddPhone}
                                disabled={qrLoading}
                            >
                                <QrCode size={18} className="sm:w-5 sm:h-5" /> 
                                {qrLoading ? t('Generating...') : t('Link New Phone')}
                            </button>
                        )}
                    </div>

                    {/* Plan info */}
                    {userPlan && (
                        <div className={`mb-8 sm:mb-12 bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-4 sm:p-6 flex items-start gap-3 sm:gap-4 text-sm sm:text-base ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <AlertCircle size={20} className="sm:w-6 sm:h-6 text-[#128C7E] flex-shrink-0 mt-0.5" />
                            <div className={isRTL ? 'text-right' : ''}>
                                <h3 className="text-sm sm:text-base font-semibold text-white mb-1 sm:mb-2">{t('Your Plan Limit')}</h3>
                                <p className="text-xs sm:text-base text-[#A0AEC0]">{t('You can link up to {{max}} phone number(s)', { max: userPlan.max_phone_numbers })}</p>
                                <p className="text-[#A0AEC0] text-xs sm:text-sm mt-1">{t('Currently linked: {{count}}/{{max}}', { count: phones.filter(p => p.is_active).length, max: userPlan.max_phone_numbers })}</p>
                            </div>
                        </div>
                    )}

                    {/* Phone numbers list */}
                    {phones.length === 0 ? (
                        <div className="text-center py-12 sm:py-24">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#1A1D23]/40 rounded-lg sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                                <Phone size={32} className="sm:w-10 sm:h-10 text-[#A0AEC0]" />
                            </div>
                            <h3 className="text-lg sm:text-2xl font-bold text-white mb-2">{t('No phone numbers linked yet')}</h3>
                            <p className="text-xs sm:text-base text-[#A0AEC0] mb-6 sm:mb-8">{t('Add your first WhatsApp number to get started')}</p>
                            {canAddMore && (
                                <button 
                                    className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-[#128C7E] text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20"
                                    onClick={handleAddPhone}
                                >
                                    <QrCode size={16} className="sm:w-5 sm:h-5" /> {t('Link Your First Phone')}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4 sm:space-y-8">
                            {phones.map(phone => (
                                <div key={phone.id} className="bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl overflow-hidden hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                                    {/* Phone Header Section */}
                                    <div className="bg-gradient-to-r from-[#128C7E]/20 to-transparent p-4 sm:p-8 border-b border-[#2D3748]">
                                        <div className="space-y-4 sm:space-y-6">
                                            {/* Phone Info Row */}
                                            <div className={`flex items-center gap-3 sm:gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#128C7E]/30 rounded-lg sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                                                    <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-[#128C7E]" />
                                                </div>
                                                <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
                                                    <h2 className="text-xl sm:text-3xl font-bold text-white break-all mb-2 sm:mb-3">{phone.phone_number}</h2>
                                                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                                                        <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                                                            {phone.status === 'verified' ? (
                                                                <>
                                                                    <CheckCircle size={14} className="sm:w-4.5 sm:h-4.5 text-green-400" />
                                                                    <span className="text-xs sm:text-sm text-green-400 font-semibold">{t('Verified')}</span>
                                                                </>
                                                            ) : phone.status === 'disconnected' ? (
                                                                <>
                                                                    <AlertCircle size={14} className="sm:w-4.5 sm:h-4.5 text-red-400" />
                                                                    <span className="text-xs sm:text-sm text-red-400 font-semibold">{t('Disconnected')}</span>
                                                                </>
                                                            ) : phone.status === 'paused' ? (
                                                                <>
                                                                    <Power size={18} className="text-yellow-400" />
                                                                    <span className="text-sm text-yellow-400 font-semibold">{t('Paused')}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <AlertCircle size={18} className="text-[#A0AEC0]" />
                                                                    <span className="text-sm text-[#A0AEC0] font-semibold">{t(phone.status)}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                                                            <Calendar size={16} />
                                                            <span>{new Date(phone.created_at.replace(' ', 'T')).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Buttons Row */}
                                            <div className={`flex flex-wrap gap-3 pt-4 border-t border-[#128C7E]/20 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                {/* Rescan QR - Always Available */}
                                                <button 
                                                    className={`flex items-center gap-2 px-5 py-2.5 bg-[#128C7E] text-white rounded-lg font-semibold hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                    onClick={() => handleReconnectPhone(phone.id)}
                                                    disabled={qrLoading && reconnectingPhoneId === phone.id}
                                                    title={t('Rescan or change your phone connection')}
                                                >
                                                    <QrCode size={18} />
                                                    {qrLoading && reconnectingPhoneId === phone.id ? t('Scanning...') : t('Rescan QR Code')}
                                                </button>

                                                {/* Pause Phone */}
                                                {phone.status === 'verified' && (
                                                    <button 
                                                        className={`flex items-center gap-2 px-5 py-2.5 bg-orange-500/20 border border-orange-500/30 text-orange-400 rounded-lg font-semibold hover:bg-orange-500/30 transition-all duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                        onClick={() => handlePausePhone(phone.id)}
                                                        title={t('Temporarily disable this phone')}
                                                    >
                                                        <Power size={18} />
                                                        {t('Pause Phone')}
                                                    </button>
                                                )}

                                                {/* Resume Phone */}
                                                {phone.status === 'paused' && (
                                                    <button 
                                                        className={`flex items-center gap-2 px-5 py-2.5 bg-green-500/20 border border-green-500/30 text-green-400 rounded-lg font-semibold hover:bg-green-500/30 transition-all duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                        onClick={() => handleResumePhone(phone.id)}
                                                        title={t('Reactivate this phone')}
                                                    >
                                                        <Power size={18} /> {t('Resume Phone')}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-8">
                                        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 ${isRTL ? 'text-right' : ''}`}>
                                            {/* Left Column - API Key */}
                                            <div>
                                                <h3 className={`text-white font-bold text-lg mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                    <Key size={20} className="text-[#128C7E]" />
                                                    {t('API Key')}
                                                </h3>
                                                
                                                {phone.api_key && (
                                                    <div className="space-y-4">
                                                        <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                            <div className="flex-1 bg-[#0B0E14] rounded-lg p-4 border border-[#2D3748] overflow-auto">
                                                                <code className="text-[#A0AEC0] text-xs font-mono break-all">{phone.api_key}</code>
                                                            </div>
                                                            <button 
                                                                className={`flex items-center justify-center gap-2 px-4 py-4 bg-[#128C7E] text-white rounded-lg font-semibold hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                                onClick={() => handleCopyKey(phone.api_key)}
                                                                title={t('Copy API Key to clipboard')}
                                                            >
                                                                <Copy size={18} />
                                                            </button>
                                                        </div>
                                                        <div className="bg-[#128C7E]/10 border border-[#128C7E]/20 rounded-lg p-3 space-y-2">
                                                            <p className="text-[#A0AEC0] text-xs font-semibold">{t('Type:')} <span className="text-[#128C7E]">{phone.is_private ? '🔒 Private' : '🔑 Public'}</span></p>
                                                            <p className="text-[#A0AEC0] text-xs font-semibold">{t('Endpoint:')} <span className="text-[#128C7E] font-mono text-[10px]">{phone.is_private ? '/v1/private/send-otp' : '/v2/send-otp'}</span></p>
                                                        </div>
                                                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                                            <p className="text-red-400 text-xs font-semibold">⚠️ {t('Keep secure. Never share publicly.')}</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Right Column - CURL Example */}
                                            {phone.curl_example && (
                                                <div>
                                                    <h3 className={`text-white font-bold text-lg mb-4 flex items-center ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'}`}>
                                                        <span className="flex items-center gap-2">
                                                            ⚡ {t('Quick Start')}
                                                        </span>
                                                        <span className="bg-[#0B0E14] px-2 py-1 rounded text-xs font-mono text-[#A0AEC0] border border-[#2D3748]">curl</span>
                                                    </h3>
                                                    <div className="space-y-4">
                                                        <p className="text-[#A0AEC0] text-sm">{t('Test your integration with this command:')}</p>
                                                        <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2D3748] overflow-auto max-h-56">
                                                            <pre className="text-[#00FF00] text-xs font-mono whitespace-pre-wrap break-words leading-relaxed">{phone.curl_example}</pre>
                                                        </div>
                                                        <button 
                                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#128C7E] text-white rounded-lg font-semibold hover:bg-[#0DA699] transition-all duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                                                            onClick={() => handleCopyCurl(phone.curl_example)}
                                                        >
                                                            <Copy size={16} />
                                                            {t('Copy Command')}
                                                        </button>
                                                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                                            <p className="text-blue-400 text-xs">{t('Paste in terminal or use in application code')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Add Phone Form Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className={`bg-[#1A1D23]/90 backdrop-blur-lg border border-[#2D3748] rounded-2xl w-full max-w-lg mx-4 shadow-2xl ${isRTL ? 'text-right' : ''}`}>
                        <div className={`flex items-center justify-between p-8 border-b border-[#2D3748] ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <h3 className="text-2xl font-bold text-white">{t('Link New WhatsApp Phone')}</h3>
                            <button 
                                className="text-[#A0AEC0] hover:text-white transition-colors"
                                onClick={closeAddModal}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-8">
                            {error && (
                                <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
                                    <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-red-400 text-sm">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmitAddPhone} className="space-y-6">
                                <div>
                                    <label className="block text-white font-semibold mb-3">{t('Session ID')}</label>
                                    <input 
                                        type="text"
                                        value={sessionId}
                                        readOnly
                                        placeholder={t('Auto-generated')}
                                        className="w-full bg-[#0B0E14] border border-[#2D3748] rounded-xl px-4 py-3 text-[#A0AEC0] font-mono text-sm focus:border-[#128C7E] focus:outline-none transition-colors cursor-default"
                                    />
                                    <p className="text-[#A0AEC0] text-xs mt-2">{t('Auto-generated unique ID')}</p>
                                </div>

                                <div className="bg-[#128C7E]/10 border border-[#128C7E]/20 rounded-xl p-4">
                                    <p className="text-[#A0AEC0] text-sm">{t('📲 After registering, scan the QR code with WhatsApp. The phone number will be detected automatically.')}</p>
                                </div>

                                <div className="flex gap-3 justify-end pt-4">
                                    <button 
                                        type="button"
                                        className="px-6 py-2.5 bg-[#1A1D23] border border-[#2D3748] text-[#A0AEC0] rounded-lg font-semibold hover:border-[#128C7E]/50 transition-colors disabled:opacity-50"
                                        onClick={closeAddModal}
                                        disabled={formLoading}
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button 
                                        type="submit"
                                        className="px-6 py-2.5 bg-[#128C7E] text-white rounded-lg font-semibold hover:bg-[#0DA699] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-48"
                                        disabled={formLoading}
                                    >
                                        {!formLoading ? (
                                            <>
                                                <QrCode size={18} />
                                                {t('Register & Generate QR')}
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-2">
                                                    {registrationStep === 1 && (
                                                        <>
                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            <span>{t('Registering...')}</span>
                                                        </>
                                                    )}
                                                    {registrationStep === 2 && (
                                                        <>
                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            <span>{t('Generating QR...')}</span>
                                                        </>
                                                    )}
                                                    {registrationStep === 3 && (
                                                        <>
                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            <span>{t('Opening QR...')}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {showQRModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-[#1A1D23]/90 backdrop-blur-lg border border-[#2D3748] rounded-2xl w-full max-w-md mx-4 shadow-2xl">
                        <div className="flex items-center justify-between p-8 border-b border-[#2D3748]">
                            <h3 className="text-2xl font-bold text-white">
                                {reconnectingPhoneId ? t('🔄 Reconnect WhatsApp') : t('Scan QR Code')}
                            </h3>
                            <button 
                                className="text-[#A0AEC0] hover:text-white transition-colors"
                                onClick={closeQRModal}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-8 flex flex-col items-center justify-center min-h-96">
                            {(qrLoading || !qrCode) && (
                                <div className="flex flex-col items-center gap-6 w-full">
                                    <div className="w-16 h-16 border-4 border-[#2D3748] border-t-[#128C7E] rounded-full animate-spin"></div>
                                    <div className="text-center space-y-2">
                                        <p className="text-[#A0AEC0] font-semibold">
                                            {reconnectingPhoneId 
                                                ? t('Generating new QR code...') 
                                                : t('Loading QR code...')
                                            }
                                        </p>
                                        <p className="text-[#A0AEC0] text-xs">{t('This may take a moment')}</p>
                                    </div>
                                    {/* Progress indicator */}
                                    <div className="w-full bg-[#2D3748] rounded-full h-1 overflow-hidden">
                                        <div className="bg-[#128C7E] h-full animate-pulse" style={{width: '100%'}}></div>
                                    </div>
                                    <p className="text-[#A0AEC0] text-xs text-center max-w-xs">
                                        {t('Make sure you have WhatsApp installed on your phone and your internet connection is stable')}
                                    </p>
                                </div>
                            )}

                            {!qrLoading && qrCode && (
                                <div className="flex flex-col items-center gap-6">
                                    <div className="bg-white p-6 rounded-2xl shadow-xl">
                                        <QRCodeSVG 
                                            value={qrCode}
                                            size={280}
                                            level="H"
                                            includeMargin={true}
                                            renderAs="svg"
                                        />
                                    </div>
                                    <div className="text-center space-y-2">
                                        <p className="text-[#128C7E] text-sm font-semibold">✅ {t('QR Code Ready')}</p>
                                        <p className="text-[#A0AEC0] text-sm">
                                            {t('Scan this QR code with WhatsApp Linked Devices')}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {!qrLoading && !qrCode && (
                                <div className="flex flex-col items-center gap-4 text-center">
                                    <AlertCircle size={48} className="text-red-400" />
                                    <div className="space-y-2">
                                        <p className="text-red-400 font-semibold">{t('QR Code Failed to Load')}</p>
                                        <p className="text-[#A0AEC0] text-sm">{t('The server took too long to respond. Please try again.')}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-8 border-t border-[#2D3748] flex gap-3 justify-end">
                            <button 
                                className="px-6 py-2.5 bg-[#1A1D23] border border-[#2D3748] text-[#A0AEC0] rounded-lg font-semibold hover:border-[#128C7E]/50 transition-colors"
                                onClick={closeQRModal}
                            >
                                {t('Close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal - Show API Key and CURL */}
            {showSuccessModal && generatedApiKey && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-[#1A1D23]/90 backdrop-blur-lg border border-[#2D3748] rounded-2xl w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-8 border-b border-[#2D3748] sticky top-0 bg-[#1A1D23]/80 backdrop-blur-lg">
                            <h3 className="text-2xl font-bold text-white">🎉 {t('Phone Linked Successfully!')}</h3>
                            <button 
                                className="text-[#A0AEC0] hover:text-white transition-colors"
                                onClick={closeSuccessModal}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            {/* Success Message */}
                            <div className="text-center space-y-3">
                                <div className="flex justify-center">
                                    <CheckCircle size={48} className="text-green-400" />
                                </div>
                                <h4 className="text-xl font-bold text-white">{t('Your WhatsApp phone is now connected!')}</h4>
                                <p className="text-[#A0AEC0]">{t('Your private API key has been generated. Use it to send OTP from this specific phone.')}</p>
                            </div>

                            {/* Key Type Badge */}
                            <div className="flex items-center justify-center gap-3">
                                <span className="bg-[#128C7E]/20 border border-[#128C7E]/30 text-[#128C7E] px-3 py-1 rounded-lg text-xs font-semibold">🔒 {t('Private Key')}</span>
                                <span className="bg-[#1A1D23]/60 border border-[#2D3748] text-[#A0AEC0] px-3 py-1 rounded-lg text-xs font-mono">/v1/private/send-otp</span>
                            </div>

                            {/* API Key Card */}
                            <div className="bg-[#1A1D23]/40 border border-[#2D3748] rounded-xl p-6 space-y-3">
                                <label className="block text-white font-semibold text-sm">{t('API Key')}</label>
                                <div className="flex gap-3">
                                    <div className="flex-1 bg-[#0B0E14] rounded-lg p-4 border border-[#2D3748]">
                                        <code className="text-[#A0AEC0] text-xs font-mono break-all">{generatedApiKey}</code>
                                    </div>
                                    <button 
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-[#128C7E] text-white rounded-lg font-semibold hover:bg-[#0DA699] transition-colors flex-shrink-0"
                                        onClick={handleCopyGeneratedKey}
                                        title={t('Copy API Key')}
                                    >
                                        <Copy size={18} />
                                        {copiedKey === generatedApiKey && <span className="text-xs">✓</span>}
                                    </button>
                                </div>
                                <p className="text-[#A0AEC0] text-xs">{t('Keep this key safe. Use it to authenticate API requests.')}</p>
                            </div>

                            {/* CURL Card */}
                            {generatedCurlExample && (
                                <div className="bg-[#1A1D23]/40 border border-[#2D3748] rounded-xl p-6 space-y-3">
                                    <label className="block text-white font-semibold text-sm">{t('Send OTP Example (CURL)')}</label>
                                    <div className="flex gap-3">
                                        <div className="flex-1 bg-[#0B0E14] rounded-lg p-4 border border-[#2D3748] overflow-x-auto">
                                            <code className="text-[#00FF00] text-xs font-mono whitespace-pre-wrap break-words">{generatedCurlExample}</code>
                                        </div>
                                        <button 
                                            className="flex items-center justify-center gap-2 px-4 py-2 bg-[#128C7E] text-white rounded-lg font-semibold hover:bg-[#0DA699] transition-colors flex-shrink-0"
                                            onClick={handleCopyGeneratedCurl}
                                            title={t('Copy CURL command')}
                                        >
                                            <Copy size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Next Steps */}
                            <div className="bg-[#128C7E]/10 border border-[#128C7E]/20 rounded-xl p-6">
                                <h4 className="font-bold text-white mb-3">{t('Next Steps')}</h4>
                                <ul className="space-y-2 text-[#A0AEC0] text-sm">
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#128C7E] font-bold flex-shrink-0">1.</span>
                                        <span>{t('Copy your API key and store it safely')}</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#128C7E] font-bold flex-shrink-0">2.</span>
                                        <span>{t('This private key sends OTP from your linked WhatsApp phone')}</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#128C7E] font-bold flex-shrink-0">3.</span>
                                        <span>{t('Use the CURL example to test sending OTP')}</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#128C7E] font-bold flex-shrink-0">4.</span>
                                        <span>{t('Just provide phone number, OTP, and optional title')}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="p-8 border-t border-[#2D3748] flex gap-3 justify-end sticky bottom-0 bg-[#1A1D23]/80 backdrop-blur-lg">
                            <button 
                                className="w-full px-6 py-3 bg-[#128C7E] text-white rounded-lg font-semibold hover:bg-[#0DA699] transition-colors"
                                onClick={closeSuccessModal}
                            >
                                {t('Done')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </DashboardLayout>
    );
}
